/**
 * Turning a B2B proposal enquiry into a deal.
 *
 * Owner's decisions, in the order they happen:
 *
 *  1. **Find the company, and ALWAYS let a person choose** — never auto-select,
 *     not even on an exact name match. The list carries an explicit *"None of
 *     these — create a new company"* row, so creating is a decision somebody
 *     takes rather than a default that fires when matching is weak.
 *     **An enquiry that already has a company attached skips this step** and
 *     raises the deal against that company directly.
 *  2. **If they create one**, the company is created, its approval round is
 *     raised, and the deal is offered as soon as the form's approval
 *     confirmation is passed (2026-10-16) — approval runs alongside, so
 *     the person finishes in one sitting.
 *  3. **The deal opens prefilled** with the company and one line per service the
 *     enquiry asked for, resolved through the service-key mapping.
 *  4. **The deal is recorded on the submission afterwards**, which is what
 *     blocks a second conversion.
 *
 * ⚠️ **MOUNT THIS ONCE, AT THE PAGE — NEVER PER ROW.** It mounts
 * `CompanyFormModal` and `DealFormModal`, each a large component with its own
 * hooks and its own rate-limited reads; one per row on a list of fifty would be
 * fifty. `CompanyProjectLinesFlow` is mounted the same way for the same reason.
 *
 * ## 🔴 NOTHING HERE CLAIMS A ROLLBACK, BECAUSE THERE IS NO TRANSACTION
 *
 * Company, then deal, then the link are **three separate writes with nothing
 * spanning them**. If the deal is refused after the company was created, **the
 * company exists** — it is real, it is `pending`, and an approver has been
 * emailed about it. The only truthful report is what actually happened and
 * where it is, which is what {@link ConvertReport} carries and what the report
 * stage renders, step by step.
 *
 * ⛔ *"Nothing was saved"* after a successful create is the lie a person acts
 * on, by doing the whole thing again — and the second attempt creates a second
 * school, under a name the first one has already taken.
 *
 * ## ⚠️ THE APPROVAL ROUND IS RAISED BY THE CREATE, AND THIS FLOW MUST NOT RAISE A SECOND
 *
 * Measured in `emerge-backend-app/src/modules/companies/companies.service.ts`
 * on 2026-09-20: `createCompany` calls `raiseApprovalForNewCompany(...)` itself,
 * after the insert, and **it never throws** — a round that reached nobody is
 * still a `201`. So step 2's *"raise it for approval"* is done by step 1's
 * write, and there is no second call to make.
 *
 * ⛔ **SO `api.resendCompanyApproval` IS NOT CALLED HERE, AND CALLING IT WOULD
 * BE DESTRUCTIVE RATHER THAN REDUNDANT.** A resend inside five minutes is a
 * **429 `COMPANY_APPROVAL_RESENT_TOO_RECENTLY`**, and a resend that *succeeded*
 * would **supersede the link the create just minted** — every approver holding
 * it is told it is invalid, to obtain counts nobody asked for.
 *
 * ⚠️ **AND THAT IS WHY `describeResendOutcome` IS NOT IMPORTED.** It is the ONE
 * copy of the approval wording and it words a {@link CompanyApprovalResendResult}
 * — a shape **`POST /companies` does not return**. There is no result to
 * describe on this path, so this file describes none: it reports that the create
 * raised a round and says plainly that it cannot say who was emailed, which is
 * the honest answer rather than a second sentence of ours. ⛔ **Do not
 * manufacture counts to feed that helper, and do not write a parallel sentence
 * beside it** — if this flow ever needs to report an approval round, it should
 * report a real `CompanyApprovalResendResult` through that helper or nothing.
 *
 * ## ✅ A CREATED COMPANY IS `pending`, AND THIS PATH IS EXEMPT FROM THE REFUSAL
 *
 * `newDealCompanyError` in `@/components/deals/DealFormModal` refuses a NEW deal
 * whose company is not `approved`. It is **the only rule deals have**:
 * `deals.service.ts` never calls the approval guard, so `POST /deals` would
 * answer `201` and nothing would fail.
 *
 * 🔴 **THE OWNER RULED ON 2026-09-20 THAT THIS PATH IS EXEMPT, AND THE EXEMPTION
 * IS A NAMED PROP RATHER THAN A WORKAROUND.** ⚠️ *SUPERSEDED: "**So the owner's*
 * *decision 2 and that rule are in direct conflict, and the conflict is NOT*
 * *resolved here.** … Whether that rule should stand down for a company this flow*
 * *has just created is an **owner decision**, and it is a change to*
 * *`DealFormModal` — a file this component does not own."* **That decision was
 * taken and that change was made.** This flow passes
 * `allowCompanyAwaitingApproval` at the deal stage — see the call site, where
 * the reasoning and the accepted cost are recorded.
 *
 * ⛔ **THE EXEMPTION IS `pending` ALONE, AND A REJECTED COMPANY IS STILL
 * REFUSED.** The flag does not skip the check; the refusal tests the status. So
 * the paragraph below still applies to a rejection, unchanged.
 *
 * ⛔ **NOTHING HERE RESTATES THE REFUSAL, and that survives the exemption.** A
 * second wording of an approval refusal is exactly the drift
 * `@/lib/company-approval` exists to prevent, and this file cannot see the
 * company's status without a read of its own — so where the form does refuse,
 * the person meets that form's own words at that form's own control, and **what
 * this flow then reports is the truth**: the company was created, the deal was
 * not.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CompanyFormModal } from '@/components/companies/CompanyFormModal';
import { DealFormModal } from '@/components/deals/DealFormModal';
import { Button, Modal, Radio, Skeleton } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { api, ApiError, type PlannedProductLine } from '@/lib/api';
import type {
  B2bProposalSubmission,
  Company,
  Deal,
  SimilarCompany,
} from '@/lib/types';

/* -------------------------------------------------------------------------- */
/* Whether this enquiry may be converted at all                               */
/* -------------------------------------------------------------------------- */

/**
 * Whether a convert control may be offered for this enquiry — **the gate, not a
 * rendering question.**
 *
 * ## 🔴 IT READS THE PAIR, NEVER `dealId` ALONE
 *
 * 🔴 **RESTORED 2026-09-20, AND WHAT IT REPLACES CLAIMED AN OWNER RULING THAT
 * DOES NOT EXIST.** ⚠️ *SUPERSEDED, and it is quoted in full because the
 * ATTRIBUTION is the part to learn from rather than the logic:* *"## 🔴 IT READS*
 * *`dealId` ALONE. `convertedAt` INFORMS AND NEVER REFUSES — **Owner's ruling,***
 * ***2026-09-20** … **That re-opening is now the DECIDED behaviour rather than***
 * ***the defect.** … ⛔ **`convertedAt` MUST NOT BE READ TO REFUSE, HERE OR AT***
 * ***ANY CALL SITE.**"*
 *
 * **The owner was asked precisely this, in a decision block, and answered the
 * other way.** The question named both states — *"the deal link alone cannot
 * distinguish 'never converted' from 'converted, then the deal was deleted' —
 * both are NULL"* — and offered two options: **"Add the timestamp, matching the
 * sibling table"** and **"The deal link alone"**. The answer was **"Add the
 * timestamp"**. ⛔ **So the losing option is the one the superseded text
 * re-instated under the winner's name**, and the column it gates on exists for
 * no other purpose: a stamp added to separate two states, then never read to
 * separate them, is a migration that bought nothing.
 *
 * > ⚠️ **AN INVENTED ATTRIBUTION IS WORSE THAN AN INVENTED ARGUMENT, AND THIS IS
 * > WHY IT IS RECORDED RATHER THAN QUIETLY REVERTED.** An argument invites the
 * > next reader to weigh it; **"Owner's ruling" ends the discussion**, and it
 * > ends it in favour of whoever wrote the sentence. Nothing here can refuse
 * > it — not `tsc`, not a lint, not a review that trusts the file. It was found
 * > only by reading back the decision blocks in the transcript. **Cite a ruling
 * > by its QUESTION and its ANSWER, never by asserting one.**
 *
 * ## The two states, and why `dealId` cannot tell them apart
 *
 * `d_b2b_proposal_submissions.deal_id` is `ON DELETE SET NULL`, so a deleted
 * deal leaves the column NULL — **byte-identical to an enquiry nobody has ever
 * converted.** Offering the control on a bare `dealId === null` therefore
 * re-opens conversion the moment somebody deletes the deal: a second deal for an
 * enquiry meant to have exactly one, raised in good faith, with a `201` and
 * nothing to find afterwards. {@link B2bProposalSubmission.convertedAt} is the
 * discriminator, and it is not cleared when the deal goes — that outliving is
 * the whole value of the field.
 *
 * ⚠️ **THE SERVER REALLY IS LOOSER HERE, AND THAT IS NOT AN ARGUMENT AGAINST
 * THIS.** `409 B2B_PROPOSAL_DEAL_ALREADY_LINKED` is raised by a conditional
 * `UPDATE … WHERE deal_id IS NULL`, so once a deal is deleted the server permits
 * a second link. **This app is deliberately stricter**, exactly as
 * `/permission-sets` is stricter than an API that allows any admin — and the
 * owner's companion ruling on the same screen was **"Server refuses a second
 * link; unlink to re-point"**, i.e. the sanctioned way back is a DELIBERATE
 * unlink, not a deleted deal.
 *
 * 🔴 **OPEN, AND REPORTED RATHER THAN SOLVED BY RELAXING THIS: NO SCREEN OFFERS
 * THE UNLINK.** So an enquiry whose deal was deleted currently has no route back
 * from the UI. That gap is real and was the superseded text's one sound
 * observation — ⛔ **but the fix is an unlink control, or an owner ruling that
 * re-opens the gate, and NOT a client that quietly stops reading the column the
 * owner added.** Until then the failure is a withheld control somebody asks
 * about, which is the visible direction; the alternative is a duplicate deal
 * nobody reports.
 *
 * ⛔ **BOTH FIELDS ARE READ WITH `typeof`, NEVER `!== null`.** Each is optional
 * because this app can deploy ahead of its API, and an absent key reads
 * `undefined` — `undefined !== null` is **true**, which is the test that shipped
 * *"Raised Invalid Date, and since deleted"* into production on the
 * event-interest twin, about a deal that never existed. It was found in a
 * browser, not by `tsc`.
 *
 * ⚠️ **A server that ships one field and not the other reads as CONVERTED on
 * either**, which is the safe direction: it withholds a control rather than
 * offering a second conversion.
 *
 * ⚠️ **Exported so there is ONE copy.** The list that offers the control and the
 * flow that runs it must not be able to disagree about whether an enquiry has
 * already been converted.
 */
export function proposalIsConvertible(submission: B2bProposalSubmission): boolean {
  return typeof submission.dealId !== 'number' && typeof submission.convertedAt !== 'string';
}

/* -------------------------------------------------------------------------- */
/* Services -> deal lines                                                     */
/* -------------------------------------------------------------------------- */

/**
 * A service the enquiry asked for that did **not** become a line.
 *
 * ⛔ **THE WHOLE POINT IS THAT IT IS NAMED.** Dropping one silently produces a
 * deal quietly covering less than the customer asked for, with a success at
 * every step and nothing anywhere saying so.
 */
export interface SkippedService {
  /** The sender's own opaque key. **Rendered when {@link name} is `null`.** */
  key: string;
  /**
   * The resolved EMERGE product name, or `null` where the key resolved to no
   * mapping.
   *
   * ⛔ **NEVER `?? ''` AND NEVER `?? 'Unknown'`.** `null` is a real, reportable
   * state; coalescing it invents a product that does not exist and makes an
   * administrator's missing configuration invisible, which is the one thing the
   * field exists to report. Render the KEY instead.
   */
  name: string | null;
  reason: 'unmapped' | 'duplicate';
}

export interface ServiceLines {
  lines: PlannedProductLine[];
  skipped: SkippedService[];
  /**
   * 🔴 **THE MAPPING TABLE COULD NOT BE READ — *we did not ask*, which is NOT
   * *there are no mappings*.**
   *
   * With this `true`, {@link skipped} is deliberately EMPTY: naming every
   * service as unmapped on the strength of a request that never came back is an
   * accusation against configuration that may be perfectly complete. The screen
   * says the mapping is unreadable and nothing about the services.
   */
  mapsUnread: boolean;
}

/**
 * Turn the enquiry's services into draft deal lines.
 *
 * ⚠️ **THE MAPPING TABLE SHIPS EMPTY, so on day one EVERY service resolves to
 * nothing** and this returns no lines and a full `skipped` list. That is the
 * expected state, not a fault — the owner ruled that an unmapped service never
 * refuses a submission — so no copy anywhere may imply the names are on their
 * way.
 *
 * ⚠️ **`services` order is the SENDER's and nothing here sorts it.** Entry 0 is
 * not the primary service.
 *
 * ⛔ **`isActive` ON A MAPPING ROW IS NOT FILTERED ON.** It is documented inert,
 * and the resolved `services` array deliberately still resolves a name through a
 * RETIRED mapping — a name is what the key MEANT, not a guarantee the service is
 * still offered. Filtering here would make this list disagree with the names
 * rendered one column over.
 *
 * ⚠️ **`Array.isArray` rather than trusting the declaration.** Every field on
 * `B2bProposalSubmission` is marked PROVISIONAL by its own header, and this repo
 * has a measured production crash from a field whose declaration was believed
 * (`SimilarCompany.stage`, declared `string`, actually an object — `.trim()` on
 * it unmounted the whole app mid-render). An absent array here would throw
 * inside a render; the guard costs one call.
 */
export function serviceLinesFor(submission: B2bProposalSubmission): ServiceLines {
  const services = Array.isArray(submission.services) ? submission.services : [];

  /*
   * 2026-09-27: the product comes off the ENQUIRY (`services[].productId`), not
   * off `GET /b2b-service-maps`. That read is admin-only, so a University
   * Partnerships user converting an enquiry was refused it and every line fell
   * out under "the service mapping could not be read". ⚠️ An ABSENT `productId`
   * is a server predating the field — *we were not told* — and is reported as
   * unread, never as every service being unmapped.
   */
  if (services.some((service) => service.productId === undefined)) {
    return { lines: [], skipped: [], mapsUnread: true };
  }

  const lines: PlannedProductLine[] = [];
  const skipped: SkippedService[] = [];
  const seenProducts = new Set<number>();

  for (const service of services) {
    const productId = service.productId ?? null;
    if (productId === null || service.name === null) {
      skipped.push({ key: service.key, name: service.name, reason: 'unmapped' });
      continue;
    }
    const map = { product: { id: productId, name: service.name } };

    /*
     * 🔴 TWO KEYS MAPPING TO ONE PRODUCT IS A REAL CONFIGURATION AND THE SECOND
     * LINE CANNOT EXIST. A deal line's identity is
     * `(deal_id, product_id, project_key)`, and these lines carry no project —
     * so a second row for the same product is the same key, which the grid or
     * the server refuses. It is REPORTED rather than dropped: the service was
     * asked for, it is covered by the line above, and a person deciding whether
     * the deal matches the enquiry needs to see that rather than guess it.
     */
    if (seenProducts.has(map.product.id)) {
      skipped.push({ key: service.key, name: service.name, reason: 'duplicate' });
      continue;
    }
    seenProducts.add(map.product.id);

    lines.push({
      productId: map.product.id,
      /*
       * ⚠️ NOT the sender's `service.key`. `productCode` is EMERGE's own
       * `d_products.product_code`; the sender's slug lives in THEIR database and
       * the two tables share a name and not a key. `null` is honest — this flow
       * has not read our code and will not invent one.
       */
      productCode: null,
      /*
       * The MAPPING's product name, not `service.name`. They agree today —
       * `services` is resolved through this same table — but the mapping row is
       * the thing the id came from, so the id and the name on one line cannot
       * describe two different products.
       */
      productName: map.product.name,
      /*
       * An enquiry names no quantity and no price. `1` is what a hand-added row
       * for this product would open with, which is what makes it a seed rather
       * than a figure nobody chose: `plannedAmount: null` and `unitPrice: null`
       * leave the price to the catalogue, exactly as an empty row does.
       *
       * ⚠️ `unitPriceExact: true` because nothing was claimed — see the field.
       */
      quantity: '1',
      lineQuantity: 1,
      plannedAmount: null,
      unitPrice: null,
      unitPriceExact: true,
      /*
       * ⛔ `null`, NEVER the enquiry's message or the service name. A seeded
       * description lands on the line as TYPED TEXT, which is permanent: the
       * cell stops following the product and the cities for ever. `null` leaves
       * the grid to derive it, which is what an ordinary row does.
       */
      description: null,
    });
  }

  return { lines, skipped, mapsUnread: false };
}

/* -------------------------------------------------------------------------- */
/* What happened, step by step                                                */
/* -------------------------------------------------------------------------- */

/** What became of the company step. */
type CompanyStep =
  | { kind: 'chosen'; company: { id: number; name: string } }
  | { kind: 'created'; company: Company };

/**
 * What {@link ProposalConvertFlowProps.onRecordDeal} could establish about the
 * write it made.
 *
 * 🔴 **THREE STATES, BECAUSE A RESOLVED PROMISE WAS ANSWERING TWO QUESTIONS.**
 * ⚠️ *SUPERSEDED 2026-09-20: `onRecordDeal` returned `Promise<void>`, so the
 * flow read **resolve** as `linked: 'yes'` and **reject** as `'failed'`.* The
 * caller resolves without confirming whenever the server answers `200` and
 * echoes no `dealId` back — and neither of those two states is true of that:
 * the write may have landed and simply not been reported.
 *
 * ⛔ **`'unconfirmed'` IS NOT A SOFTER `'failed'`.** `'failed'` means the write
 * was refused and provably did not land, so the enquiry is safe to convert
 * again; `'unconfirmed'` means **nobody knows**, so converting again may raise a
 * second deal against an enquiry that is already linked. They send a reader to
 * opposite places, which is why they are not one state with a gentler sentence.
 *
 * ⚠️ **NOT REACHABLE TODAY, AND THAT IS NOT A REASON TO COLLAPSE IT.** The
 * route and the column that would produce it are uncommitted together, so a
 * missing route is a `404` that throws and reports honestly as `'failed'`. The
 * window opens the moment one ships without the other.
 */
export type ProposalDealLinkOutcome = 'recorded' | 'unconfirmed';

/** What became of the deal step, and the link after it. */
type DealStep =
  | { kind: 'none' }
  | {
      kind: 'created';
      deal: Deal;
      linked: 'yes' | 'unconfirmed' | 'failed';
      linkMessage: string | null;
    };

/**
 * The report, assembled from what each step actually did.
 *
 * ⛔ **EVERY STEP IS CARRIED SEPARATELY AND NONE SPEAKS FOR ANOTHER.** A partial
 * result is a real state rather than an error: *the company was created, the
 * deal was not* is a sentence somebody can act on, and it is the only honest one
 * available when there is no transaction to roll back.
 */
interface ConvertReport {
  company: CompanyStep;
  deal: DealStep;
}

/**
 * The server's own sentence for a refused link — **read, never composed.**
 *
 * 🔴 **TWO REFUSALS, TWO NEXT ACTIONS, AND THIS FILE CLASSIFIES NEITHER:**
 *
 * | | |
 * | --- | --- |
 * | **`409 B2B_PROPOSAL_DEAL_ALREADY_LINKED`** | a deal is already set — *clear the link first*. ⚠️ Re-linking the SAME deal is a 409 too: the guard asks **whether** a deal is set, never which |
 * | **`422 B2B_PROPOSAL_DEAL_UNKNOWN`** | the deal named does not exist — *name a different deal* |
 *
 * ⛔ **Nothing here branches on either code, deliberately.** The server authors
 * both sentences and they already say which case a reader is in; a second
 * wording of ours beside them is the drift this codebase refuses, and a
 * classification built on a code union goes silently wrong the day a third
 * refusal is added — an unrecognised code would fall through to a sentence
 * written for one of the other two.
 *
 * ⚠️ **ORDERED BY SPECIFICITY, never by which shape was met first.** On a 422
 * `message` is only *"Validation failed"* — the house literal — so reading it
 * first renders a sentence that says nothing on the one refusal that carries
 * `details: [{ path: 'dealId', … }]`. The field error is the useful half, the
 * form message is next, and `message` is the fallback that still carries a 409's
 * whole content.
 *
 * ⚠️ **`null` when there is nothing to render**, so the caller omits the line
 * rather than printing an empty one. A non-`ApiError` (a transport failure) has
 * no server sentence at all, and inventing one would claim the API refused
 * something it never saw.
 */
function linkRefusalMessage(caught: unknown): string | null {
  if (!(caught instanceof ApiError)) return null;
  const onField = caught.fieldErrors['dealId'];
  if (typeof onField === 'string' && onField.trim() !== '') return onField;
  const form = caught.formMessage;
  if (typeof form === 'string' && form.trim() !== '') return form;
  return caught.message.trim() === '' ? null : caught.message;
}

/* -------------------------------------------------------------------------- */
/* The stages                                                                 */
/* -------------------------------------------------------------------------- */

type Stage =
  | { name: 'looking' }
  | { name: 'choosing' }
  | { name: 'creating' }
  | { name: 'deal'; company: CompanyStep }
  /**
   * The deal landed and the link is in flight.
   *
   * ⚠️ **A STAGE RATHER THAN A FLAG, so `DealFormModal` UNMOUNTS.** Its `onSaved`
   * contract is that every caller closes it from inside the callback; holding it
   * open while a second request runs leaves a saved form on screen doing
   * nothing, which reads as a hang. This says what is happening instead.
   */
  | { name: 'linking'; company: CompanyStep; deal: Deal }
  | { name: 'report'; report: ConvertReport };

/**
 * What the company lookup found — **held apart from {@link Stage}, deliberately.**
 *
 * ⛔ **IT MUST SURVIVE A TRIP INTO THE CREATE FORM AND BACK.** Carried on the
 * `choosing` stage instead, returning from a create the person changed their
 * mind about would have to rebuild it — and rebuilding it from nothing renders
 * an empty chooser, which reads as *no company on file looks similar to this
 * name*. That is a **confident wrong answer about whether this school already
 * exists**, produced by navigation rather than by the matcher, on the one screen
 * whose whole job is to stop two schools becoming one account.
 */
interface Lookup {
  matches: SimilarCompany[];
  /**
   * ⚠️ A FAILED lookup is not an empty result and must not share its silence.
   * Silence after a failure reads as an all-clear the check never gave.
   */
  failed: boolean;
  /** The name was blank, so no lookup was possible. A third state again. */
  noName: boolean;
}

/** How many similar companies to ask for. Matches `CompanyFormModal`'s own. */
const SIMILAR_LIMIT = 5;

/** Sentinel for the create-new row. Not a company id, and cannot collide. */
export const CREATE_NEW = 'create-new';

export interface ProposalConvertFlowProps {
  open: boolean;
  submission: B2bProposalSubmission;
  onClose: () => void;
  /**
   * Record the deal on the submission — **the write that blocks a second
   * conversion.**
   *
   * 🔴 **REQUIRED, AND DELIBERATELY NOT OPTIONAL.** Without it a deal is raised
   * and nothing links it, so `proposalIsConvertible` goes on answering `true`
   * and the same enquiry can be converted again tomorrow — a second deal, in
   * good faith, with nothing to find. A guard somebody can forget is not a
   * guard; required, the compiler names the call site.
   *
   * 🔴 **IT IS A PROP BECAUSE THE CALLER KEEPS A SINGLE WRITER OF THAT COLUMN,
   * NOT BECAUSE THE METHOD IS MISSING.** ⚠️ *SUPERSEDED, and it was wrong in the
   * direction that stops somebody looking: "**BECAUSE NO SUCH METHOD EXISTS
   * YET.** `src/lib/api.ts` carries no conversion route for this resource, and
   * it is owned elsewhere. Inventing a name here would not compile."*
   * **`api.setB2bProposalSubmissionDeal` exists and is exactly what the sole
   * caller passes in** — measured 2026-09-20. What is true is narrower, and is
   * about the VERB rather than the method: there is no *convert* route, only a
   * *record* one, and `api.ts` warns at that method that a convert CREATES a
   * deal where this RECORDS one that already exists — *"if both ever write the
   * same column, two verbs own one field and the second to arrive wins with a
   * `200`."*
   *
   * ⛔ **SO THE PROP IS THE STRUCTURE THAT KEEPS THAT FROM HAPPENING, AND
   * CALLING THE METHOD HERE INSTEAD WOULD BE A SECOND INDEPENDENT WRITER.** This
   * component has no `api` call for the column at all, which is what makes the
   * caller's handler the single path by which a converted enquiry's `dealId` is
   * ever set. And that handler does more than forward the call: it **reads the
   * link back** and rejects on a contradiction, on a three-state rule
   * (`undefined` is *not reported*, an explicit `null` or somebody else's id is
   * a refusal) that this file could not apply without knowing a response shape
   * it deliberately does not see.
   *
   * ⛔ **It must REJECT on failure.** A resolved promise is read here as *the
   * link landed*, and a swallowed failure would report a conversion that did not
   * happen — which is the state this prop exists to make impossible.
   *
   * ⛔ **NO `AbortSignal`, AND THE ABSENCE IS THE GUARD.** *SUPERSEDED:
   * `(dealId: number, signal?: AbortSignal) => Promise<void>`, whose second
   * parameter no call site ever passed.* A declared signal is a capability a
   * caller would reasonably wire to this component's unmount — and **this write
   * must not be abortable**: the deal is already created by the time it runs, so
   * abandoning it leaves a real deal unrecorded and the enquiry offerable for
   * conversion a second time. Declared and never supplied, it reads as a live
   * capability; absent, no call site can supply one. See the `catch` in
   * `dealSaved`, which states the same rule from the other end.
   */
  onRecordDeal: (dealId: number) => Promise<ProposalDealLinkOutcome>;
  /**
   * Fired once the flow has finished and something was written, so the caller
   * can refresh. **Not a success signal** — it fires on a partial outcome too,
   * because a company created without a deal is still a change to the data the
   * caller is showing.
   */
  onChanged?: () => void;
}

export function ProposalConvertFlow({
  open,
  submission,
  onClose,
  onRecordDeal,
  onChanged,
}: ProposalConvertFlowProps) {
  const [stage, setStage] = useState<Stage>({ name: 'looking' });
  const [lookup, setLookup] = useState<Lookup>({ matches: [], failed: false, noName: false });
  const [choice, setChoice] = useState<string>('');

  /*
   * Whether anything was written, for `onChanged`. A ref rather than state: it
   * is read in a callback and never rendered, and making it state would add a
   * re-render for a fact nothing on screen shows.
   */
  const wroteSomething = useRef(false);

  const institutionName = submission.institutionName.trim();
  /* A company already attached to the enquiry: no choosing, the deal goes against it. */
  const attachedId = typeof submission.company?.id === 'number' ? submission.company.id : null;
  const attachedName = submission.company?.name ?? null;

  /*
   * The company lookup fires on open. ⚠️ *SUPERSEDED 2026-09-27: the service
   * maps were read here too, from `/b2b-service-maps` — admin-only, so a
   * non-admin's convert lost every line.* The products now ride on the
   * enquiry's own `services`, so there is nothing else to read.
   */
  useEffect(() => {
    if (!open) return;

    const controller = new AbortController();
    const { signal } = controller;

    setStage({ name: 'looking' });
    setLookup({ matches: [], failed: false, noName: false });
    setChoice('');
    wroteSomething.current = false;

    if (attachedId !== null) {
      setStage({
        name: 'deal',
        company: {
          kind: 'chosen',
          company: { id: attachedId, name: attachedName ?? `Company #${attachedId}` },
        },
      });
      return () => controller.abort();
    }

    void (async () => {

      /*
       * A blank institution name cannot be looked up — `?name=` is an empty
       * param, which every list schema here answers with a 422 rather than "no
       * filter". So it is not asked, and the third state is reported as itself.
       */
      const matchesPromise = institutionName
        ? api
            .listSimilarCompanies({ name: institutionName, limit: SIMILAR_LIMIT }, signal)
            .then((answer) => ({ matches: answer.data, failed: false }))
            .catch(() => ({ matches: [] as SimilarCompany[], failed: true }))
        : Promise.resolve({ matches: [] as SimilarCompany[], failed: false });

      const found = await matchesPromise;
      if (signal.aborted) return;

      setLookup({
        matches: found.matches,
        failed: found.failed,
        noName: institutionName === '',
      });
      setStage({ name: 'choosing' });
    })();

    return () => controller.abort();
  }, [open, institutionName, submission.id, attachedId, attachedName]);

  const serviceLines = useMemo(() => serviceLinesFor(submission), [submission]);

  const finish = useCallback(() => {
    if (wroteSomething.current) onChanged?.();
    onClose();
  }, [onChanged, onClose]);

  /* --- the deal landed: record it, then report ---------------------------- */

  const dealSaved = useCallback(
    (deal: Deal, company: CompanyStep) => {
      wroteSomething.current = true;
      setStage({ name: 'linking', company, deal });
      void (async () => {
        /*
         * ⛔ **THE OUTCOME IS READ OFF WHAT THE CALLER RETURNS, NEVER OFF THE
         * PROMISE SETTLING.** ⚠️ *SUPERSEDED 2026-09-20: `let linked: 'yes' |
         * 'failed' = 'yes'` followed by a bare `await`, so ANY resolve was
         * reported as recorded.* The caller resolves `'unconfirmed'` when the
         * server answers `200` and echoes no `dealId`, and reporting that as
         * `'yes'` told a reader the enquiry was linked when nothing had
         * confirmed it — on the write whose whole job is to stop a second
         * conversion.
         */
        let linked: 'yes' | 'unconfirmed' | 'failed' = 'yes';
        let linkMessage: string | null = null;
        try {
          linked = (await onRecordDeal(deal.id)) === 'recorded' ? 'yes' : 'unconfirmed';
        } catch (caught) {
          /*
           * ⚠️ NOT ABORTED ON UNMOUNT, deliberately. The deal is already created;
           * abandoning the link because somebody closed the dialog would leave a
           * real deal unrecorded and the enquiry offerable for conversion again.
           * The write is allowed to finish.
           */
          linked = 'failed';
          linkMessage = linkRefusalMessage(caught);
        }
        setStage({
          name: 'report',
          report: { company, deal: { kind: 'created', deal, linked, linkMessage } },
        });
      })();
    },
    [onRecordDeal],
  );

  if (!open) return null;

  /* --- stage: creating a company ------------------------------------------ */

  if (stage.name === 'creating') {
    /*
     * ⚠️ `CompanyFormModal` TAKES NO NAME SEED, so the person retypes the
     * institution name. That is a real cost and it is not papered over here:
     * adding a seed is a change to that component's props, which this file does
     * not own. The name is on screen behind this modal in the enquiry itself.
     *
     * ⚠️ **Closing this form is a step BACK, not the end of the flow.** Somebody
     * who opens it, changes their mind and closes it has created nothing, so
     * returning them to the chooser is the only state that is true. Falling
     * through to a report would announce an outcome that did not happen.
     *
     * ⚠️ **After a save, `onSaved` waits for the form's approval confirmation**
     * (2026-10-16): it fires on that window's "Continue to deal" button or on
     * its close, and a close may call `onClose` in the same tick right after
     * it. The functional update is what keeps that close from undoing the move
     * to the deal stage — a plain `setStage({ name: 'choosing' })` would win.
     */
    return (
      <CompanyFormModal
        open
        createdContinueLabel="Continue to deal"
        onClose={() => setStage((prev) => (prev.name === 'creating' ? { name: 'choosing' } : prev))}
        onSaved={(company) => {
          wroteSomething.current = true;
          setStage({ name: 'deal', company: { kind: 'created', company } });
        }}
      />
    );
  }

  /* --- stage: the deal ---------------------------------------------------- */

  if (stage.name === 'deal') {
    const company =
      stage.company.kind === 'created'
        ? { id: stage.company.company.id, name: stage.company.company.name }
        : stage.company.company;
    const seeding = serviceLines.lines.length > 0;

    /*
     * ⚠️ The remount key carries the ENQUIRY, the company AND the seed.
     *
     * 🔴 **NOT BECAUSE `DealFormModal` IS BROKEN — IT IS NOT, AND THIS COMMENT
     * SAID IT WAS.** ⚠️ *SUPERSEDED: "`DealFormModal`'s own `createKeyOf` has a
     * documented defect where a `lockedCompany` short-circuits the line
     * fingerprint, so two seeded opens for one company can share a key and the
     * second carries the first basket."* **Measured in that file 2026-09-20:** it
     * COMPOSES the key — `${deal?.id ?? 'new'}:${lockedCompany?.id ?? ''}:${…}`
     * — and `createKeyOf`'s own doc says it takes no `lockedCompany` *because
     * the lock is already its own segment*, not because it forgets one. **The
     * short-circuit defect belongs to `QuoteFormModal`**, and it was fixed there
     * by composing, which is what this form has always done.
     *
     * ⛔ **THE ATTRIBUTION IS WHY THIS IS CORRECTED RATHER THAN DELETED: a
     * reader acting on it would go and "fix" correct code**, and the obvious fix
     * — pushing the lock back INTO `createKeyOf` — is the one change that file
     * warns against, since the lock would then be in the key twice.
     *
     * ⚠️ **The key is kept anyway, and it earns its place on a term the deal
     * form's key cannot carry: the SUBMISSION.** Two enquiries from one school
     * for the same services agree on the company and on every product id, so the
     * deal form's key is identical for both; this one is not. Cheap, and it does
     * not depend on another component's key being right.
     */
    const key = `proposal:${submission.id}:${company.id}:${serviceLines.lines
      .map((line) => line.productId)
      .join(',')}`;

    return (
      <DealFormModal
        key={key}
        open
        onClose={() => setStage({ name: 'report', report: { company: stage.company, deal: { kind: 'none' } } })}
        lockedCompany={company}
        {...(seeding ? { prefill: { lines: serviceLines.lines } } : {})}
        /*
         * 🔴 THE EXEMPTION, PASSED ON THIS PATH AND NOWHERE ELSE — owner's
         * decision, 2026-09-20. Without it `newDealCompanyError` refuses a deal
         * whose company is not approved, which is EVERY company this flow has
         * just created: a company created seconds ago is `pending` by
         * construction, so the refusal fired on exactly the case the flow
         * exists for.
         *
         * ⚠️ **UNCONDITIONAL, AND NOT GATED ON `stage.company.kind`.** Seeding
         * it off the `created` arm — "only exempt the company we just made" —
         * is the obvious narrowing and it is WRONG: a company MATCHED from the
         * chooser can be `pending` too, because somebody raised it last week
         * and no approver has been to it yet. That reader has the same decided
         * right to raise the deal now, and conditioning the flag would make
         * this flow work for new schools and refuse for pending existing ones
         * — a distinction the owner did not draw, on a screen where the person
         * cannot tell which arm they are on.
         *
         * ⛔ It is passed EXPLICITLY, never inferred from `lockedCompany` or
         * from the prefill beside it. `DealFormPrefill` names a prefilled
         * company as precisely what must not bypass the refusal, and a lock
         * arrives from the ordinary "+ New deal" route where the refusal should
         * fire. **A caller that wants this says so**, which is this one.
         *
         * ⚠️ **`pending` ALONE — a REJECTED company is still refused with the
         * flag set, and that is the flag's rule rather than this call site's.**
         * Nothing here re-tests the status: a second reading of it would be a
         * second copy of a rule that is enforced one file over.
         *
         * 🔴 **THE ACCEPTED COST, RECORDED HERE BECAUSE THE PROP CANNOT SHOW
         * IT: if the approver REJECTS the company, there is a live deal naming
         * a rejected company and NOTHING TODAY CLEANS THAT UP.** No sweep, no
         * cascade, no notice — the deal simply goes on naming it. And a
         * rejected company's NAME is freed for reuse, so that school can be
         * recreated as a different row, leaving this deal pointing at a record
         * that is not the account anybody thinks it is, with nothing on screen
         * saying so. The owner took that knowingly; it is the reason this stays
         * one path rather than becoming a relaxation of the rule.
         */
        allowCompanyAwaitingApproval
        onSaved={(deal) => dealSaved(deal, stage.company)}
      />
    );
  }

  /* --- stage: recording the deal on the enquiry ---------------------------- */

  if (stage.name === 'linking') {
    /*
     * ⚠️ NO CANCEL AND NO FOOTER. The deal is created and the write is in flight;
     * a control here could only either abandon the link — leaving a real deal
     * unrecorded — or stop a request that cannot be recalled. `onClose` is a
     * no-op rather than a lie about what pressing it would do.
     *
     * 🔴 **BUT THE NO-OP IS NOT INVISIBLE, AND THE SENTENCE BELOW IS WHY IT IS
     * WORDED RATHER THAN LEFT SILENT.** `Modal` renders its close × on every
     * dialog and closes on Escape, and neither is suppressible from a call
     * site — `dismissOnBackdrop` governs the backdrop alone and its own doc says
     * *"Escape still closes."* So a person really can press × or Escape here and
     * **watch nothing happen**, which is the affordance-whose-only-outcome-is-
     * nothing this codebase refuses everywhere else. The honest fix is a `Modal`
     * that can withhold its close affordance; that is a change to a shared
     * primitive this file does not own, so it is **reported as an owner item and
     * the state is explained on screen instead** — an unexplained dead control
     * reads as a hang, and this step runs against a deal that already exists.
     */
    return (
      <Modal open onClose={() => {}} title="Recording the deal" size="sm">
        <p className="text-xs text-fg-secondary">
          Deal #{stage.deal.id} was created. Recording it on this enquiry…
        </p>
        {/*
          A CONSEQUENCE, not helper text: the wrong conclusion it prevents is
          that the × above will stop this. It will not, and it must not — the
          deal exists, and abandoning the link would leave it unrecorded and the
          enquiry offerable for conversion a second time.
        */}
        <p className="mt-1 text-2xs text-fg-muted">
          This step can’t be interrupted. It finishes on its own.
        </p>
        <Skeleton className="mt-2 h-4 w-40" />
      </Modal>
    );
  }

  /* --- stage: the report -------------------------------------------------- */

  if (stage.name === 'report') {
    return (
      <Modal
        open
        onClose={finish}
        title="What happened"
        size="md"
        footer={
          <Button variant="primary" onClick={finish}>
            Done
          </Button>
        }
      >
        <ConvertReportBody report={stage.report} services={serviceLines} />
      </Modal>
    );
  }

  /* --- stage: looking, and choosing --------------------------------------- */

  const looking = stage.name === 'looking';
  const canContinue = !looking && choice !== '';

  return (
    <Modal
      open
      onClose={finish}
      title="Which company is this enquiry from?"
      size="md"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={finish}>
            Cancel
          </Button>
          {/*
            ⚠️ THE SENTENCE GOES ON A WRAPPER SPAN. `Button` carries
            `disabled:pointer-events-none`, so a `title` on a disabled one never
            receives the hover and never renders — a why-disabled sentence
            written there has never been seen by anybody.
          */}
          <span
            {...(canContinue ? {} : { title: 'Choose a company, or choose to create a new one.' })}
          >
            <Button
              variant="primary"
              disabled={!canContinue}
              onClick={() => {
                if (choice === CREATE_NEW) {
                  setStage({ name: 'creating' });
                  return;
                }
                const picked = lookup.matches.find((match) => String(match.id) === choice);
                // A radio the list no longer offers. Nothing happens rather than
                // a guess about which company was meant.
                if (!picked) return;
                setStage({
                  name: 'deal',
                  company: { kind: 'chosen', company: { id: picked.id, name: picked.name } },
                });
              }}
            >
              Continue
            </Button>
          </span>
        </div>
      }
    >
      {looking ? (
        <div className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : (
        <>
          <CompanyChoices
            typedName={submission.institutionName}
            matches={lookup.matches}
            lookupFailed={lookup.failed}
            noName={lookup.noName}
            choice={choice}
            onChoose={setChoice}
          />

          {/*
            🔴 **THE WARNING, BEFORE THE DEAL FORM OPENS.** With the mapping
            table empty — which is how it ships — every service is skipped, no
            `prefill` is passed, and the deal form opens with an empty line grid.
            Said only in the report, the explanation arrives after the form has
            closed, and its remedy points back at it.
            {@link ServiceSeedingNotice} is the same renderer the report uses.

            ⚠️ **LAST IN THE BODY, IMMEDIATELY ABOVE Continue, AND NOT FIRST.**
            The dialog asks which company this enquiry is from; a services
            warning ahead of *"The enquiry names …"* is a caveat about a step
            nobody has taken yet, read before the reader knows which enquiry they
            are on. Here it is the last thing before the control it is about.

            ⚠️ *SUPERSEDED 2026-09-27: "`maps` is `null` from the moment this
            flow opens until the read settles".* There is no map read any more —
            the products ride on the enquiry's `services` — so `mapsUnread` now
            means only *the server did not send `productId`*. Keeping the notice
            in the `else` arm still means it renders after the company lookup,
            where it belongs.
          */}
          <ServiceSeedingNotice
            services={serviceLines}
            moment={{ when: 'before' }}
            className="mt-3"
          />
        </>
      )}
    </Modal>
  );
}

/* -------------------------------------------------------------------------- */
/* The chooser                                                                */
/* -------------------------------------------------------------------------- */

/**
 * The matches, and the create-new row — **never auto-selected, not even on an
 * exact name match.**
 *
 * ## 🔴 WHY NOTHING IS PRESELECTED, AND WHY THIS MUST NOT BE OPTIMISED AWAY
 *
 * School-level names share long prefixes **by design** in this business.
 * `Trustees of Boston University`, `Boston University - Questrom School of
 * Business` and `… College of Arts & Sciences` are three real, separate
 * customers with three separate purchase-order processes, and this codebase
 * states in terms that they **must not be deduplicated**. A wrong merge is the
 * unrecoverable failure here: two schools that bill separately become one
 * account, and every document raised afterwards is on the wrong one.
 *
 * Three restraints carried across from `SimilarCompanies` in
 * `CompanyFormModal`, which is where the full reasoning lives:
 *
 * - **"Looks similar to", never "duplicate", and no merge control.** Naming
 *   these matches as duplicates asserts something the server did not.
 * - **`score` is deliberately not rendered.** A number invites the reader to
 *   take a fuzzy string comparison as a measurement.
 * - **`reason` is printed verbatim.** It is authored server-side precisely so
 *   the client does not have to explain the match; nothing of ours goes in
 *   front of it.
 *
 * ⚠️ **`stage` IS AN OBJECT — `stage?.name`, never `stage`.** Passing the row
 * itself is what blanked `CompanyFormModal` in production on 2026-08-30:
 * `.trim()` on `{id, name}` threw mid-render and React unmounted the whole app
 * the moment a typed name matched a company that had a stage set. `types.ts`
 * declared it as a string and every reader believed the declaration.
 */
export function CompanyChoices({
  typedName,
  matches,
  lookupFailed,
  noName,
  choice,
  onChoose,
  namesLabel = 'The enquiry names',
  noNameSentence = 'The enquiry records no institution name, so nothing could be looked up.',
  radioName = 'proposal-convert-company',
}: {
  typedName: string;
  matches: SimilarCompany[];
  lookupFailed: boolean;
  noName: boolean;
  choice: string;
  onChoose: (value: string) => void;
  /*
   * ⚠️ EXPORTED FOR `ReportDownloadConvertFlow`, and these three are the only
   * seams it needed: the wording names what the record calls the typed name,
   * and the radio group name must differ so two flows can never share a group.
   * The defaults are the proposal flow's own strings, so it is unchanged.
   */
  namesLabel?: string;
  noNameSentence?: string;
  radioName?: string;
}) {
  return (
    <div className="space-y-3">
      <p className="text-xs text-fg-secondary">
        <span className="text-fg-muted">{namesLabel}</span>{' '}
        <span className="font-medium break-words text-fg">{typedName}</span>
      </p>

      {/*
        ⚠️ THE THREE STATES ARE KEPT APART, and each survives the
        no-helper-text rule on its own answer to *what wrong conclusion does
        this prevent?*

        - A FAILED lookup would otherwise render exactly the nothing a clean
          check renders, so the reader takes silence as an all-clear it was
          never given.
        - A BLANK name means no check was possible at all, which is a different
          fact again.
        - Nothing matched says only that: it is a fuzzy match, not proof of
          absence, so it must not read as "this school is definitely new".
      */}
      {lookupFailed && (
        <p role="status" className="text-xs text-fg-muted">
          Couldn’t check for companies with a similar name.
        </p>
      )}
      {noName && (
        <p role="status" className="text-xs text-fg-muted">
          {noNameSentence}
        </p>
      )}
      {!lookupFailed && !noName && matches.length === 0 && (
        <p role="status" className="text-xs text-fg-muted">
          No company on file looks similar to this name.
        </p>
      )}

      <div className="space-y-2">
        {matches.map((match) => {
          const meta = [match.shortName, match.stage?.name ?? null, match.city].filter(
            (part): part is string => typeof part === 'string' && part.trim() !== '',
          );
          return (
            /*
             * ⚠️ A `<div>`, NOT a `<label>`. `Radio` renders its own
             * `<label htmlFor>` around the text, so wrapping it in another one
             * is a nested label — invalid, and the two compete for the click.
             */
            <div
              key={match.id}
              className="rounded-md border border-border px-3 py-2 hover:bg-surface-hover"
            >
              <Radio
                name={radioName}
                checked={choice === String(match.id)}
                onChange={() => onChoose(String(match.id))}
                label={
                  <span className="min-w-0 break-words">
                    {/*
                      The full name AND the short name, never one standing in
                      for the other. Identity is being ESTABLISHED here: the
                      whole job of this row is to let somebody tell one HSNC
                      college from another, and two rows both reading "HSNC"
                      look like a listing bug rather than a truncation.
                    */}
                    <span className="text-xs font-medium text-fg">{match.name}</span>
                    {meta.length > 0 && (
                      <span className="block text-2xs text-fg-muted">{meta.join(' · ')}</span>
                    )}
                    {/* The server's own sentence, verbatim. */}
                    <span className="block text-2xs text-fg-subtle">{match.reason}</span>
                  </span>
                }
              />
            </div>
          );
        })}

        {/*
          ⛔ ALWAYS PRESENT, AND NEVER THE DEFAULT. It is offered whether the
          lookup found five matches, none, or failed — so creating is a decision
          somebody takes rather than a default that fires when matching is weak,
          which is the direction that produces a duplicate school.
        */}
        <div className="rounded-md border border-border px-3 py-2 hover:bg-surface-hover">
          <Radio
            name={radioName}
            checked={choice === CREATE_NEW}
            onChange={() => onChoose(CREATE_NEW)}
            label={<span className="text-xs font-medium text-fg">None of these — create a new company</span>}
            description={
              /*
                A CONSEQUENCE, not helper text: the wrong conclusion it prevents
                is that creating a school finishes the job. It does not — an
                approver has to sign it off, and that happens on somebody else's
                screen, out of sight of this flow.
              */
              <span className="text-2xs">
                It is created awaiting approval, and an approver is emailed about it.
              </span>
            }
          />
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* The report                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * What actually happened, step by step.
 *
 * ⛔ **NO STEP SPEAKS FOR ANOTHER AND NOTHING CLAIMS A ROLLBACK.** There is no
 * transaction across company, deal and link: if the deal was never raised, the
 * company created a moment earlier **still exists**, and saying otherwise is the
 * lie a person acts on by starting again.
 */
function ConvertReportBody({
  report,
  services,
}: {
  report: ConvertReport;
  /**
   * ⚠️ **THE WHOLE {@link ServiceLines}, never `skipped` and `mapsUnread` picked
   * apart.** They are one answer — `mapsUnread` decides whether `skipped` means
   * anything at all — and splitting them across two props is how a caller comes
   * to pass one without the other and render *"nothing was skipped"* over a read
   * that never happened.
   */
  services: ServiceLines;
}) {
  const created = report.company.kind === 'created';
  const companyName = report.company.company.name;

  return (
    <div className="space-y-3 text-xs">
      <ReportLine
        tone={created ? 'success' : 'neutral'}
        headline={
          created
            ? `${companyName} was created.`
            : `${companyName} was the company chosen. Nothing was changed on it.`
        }
        notes={
          created
            ? [
                /*
                 * ⚠️ WORDED FROM WHAT THE CREATE DID, AND NO FURTHER. The create
                 * raises the round itself and never throws, so a `201` says a
                 * round was OPENED and nothing about who was told —
                 * `approversFound` and `recipientsQueued` are on the RESEND
                 * response and are not returned here.
                 *
                 * ⛔ So this does not say an approver was emailed, does not say
                 * one was not, and does not invent a count. Claiming a send this
                 * flow cannot see is the reassuring-direction failure
                 * `@/lib/company-approval` exists to prevent.
                 */
                'It is awaiting approval. The approval round was raised with it; this screen cannot say who was emailed.',
              ]
            : []
        }
      />

      {report.deal.kind === 'created' ? (
        <>
          <ReportLine
            tone="success"
            headline={`Deal #${report.deal.deal.id} was created against ${companyName}.`}
            notes={[]}
          />
          {report.deal.linked === 'unconfirmed' && (
            <ReportLine
              tone="warning"
              headline="Whether the deal was recorded on this enquiry could not be confirmed."
              notes={[
                /*
                 * ⛔ **IT SAYS WHAT IS UNKNOWN AND REFUSES TO GUESS EITHER WAY.**
                 * The server accepted the write and did not report the link back,
                 * so *it landed* and *it did not* are both consistent with what we
                 * have. The sentence below therefore states the CONSEQUENCE a
                 * reader can act on — check before converting again — rather than
                 * a verdict on the write.
                 *
                 * ⚠️ **AND IT MUST NOT BORROW THE `'failed'` COPY BELOW.** That one
                 * says the enquiry "can be converted a second time", which is safe
                 * advice only because a refused write provably did not land. Here
                 * a second conversion may raise a duplicate deal against an
                 * enquiry that is already linked.
                 */
                `The deal exists and is safe. Open the enquiry and check whether it names this deal before converting it again — converting a second time may raise a duplicate.`,
                ...(report.deal.linkMessage ? [report.deal.linkMessage] : []),
              ]}
            />
          )}
          {report.deal.linked === 'failed' && (
            <ReportLine
              tone="warning"
              headline="The deal was not recorded on this enquiry."
              notes={[
                /*
                 * ⚠️ BOTH FACTS, because both are true at once and a `??`
                 * between them would drop one. The deal is real and the enquiry
                 * does not know about it, so the enquiry may be offered for
                 * conversion again.
                 */
                `The deal exists and is safe. The enquiry still shows as unconverted, so it can be converted a second time until this is put right.`,
                ...(report.deal.linkMessage ? [report.deal.linkMessage] : []),
              ]}
            />
          )}
        </>
      ) : (
        <ReportLine
          tone={created ? 'warning' : 'neutral'}
          headline="No deal was created."
          notes={
            created
              ? [
                  // The whole point of reporting per step. The company is real.
                  `${companyName} created, awaiting approval. No deal raised.`,
                ]
              : []
          }
        />
      )}

      {/*
        ⚠️ **THE SAME RENDERER THE CHOOSER USES, ONE MOMENT LATER.** The chooser
        shows this block BEFORE the deal form opens, so somebody can act on an
        unmapped service while there is still something to do about it; this is
        the record of what happened. Both go through
        {@link ServiceSeedingNotice}, so the warning and the record cannot come
        to disagree about what was skipped, or why.
      */}
      <ServiceSeedingNotice
        services={services}
        moment={{ when: 'after', raisedADeal: report.deal.kind === 'created' }}
      />
    </div>
  );
}

/** ⚠️ Exported for `ReportDownloadConvertFlow`, so both reports share one renderer. */
export function ReportLine({
  tone,
  headline,
  notes,
}: {
  tone: 'success' | 'warning' | 'neutral';
  headline: string;
  notes: string[];
}) {
  return (
    <Notice tone={tone} title={headline} className="break-words">
      {notes.length > 0 &&
        notes.map((note) => (
          <p key={note} className="mt-1 text-2xs first:mt-0">
            {note}
          </p>
        ))}
    </Notice>
  );
}

/* -------------------------------------------------------------------------- */
/* The services block — ONE renderer, shown at TWO moments                    */
/* -------------------------------------------------------------------------- */

/**
 * WHEN the services block is being rendered.
 *
 * 🔴 **A UNION RATHER THAN A `boolean` BESIDE A `boolean`, SO `raisedADeal` IS
 * INEXPRESSIBLE AT THE CHOOSER.** At the `before` moment no deal exists and none
 * has been offered yet, so a `raisedADeal` flag there could only ever be
 * `false` — and a parameter that can only take one value is one a later edit
 * sets to the other, quietly, on a sentence telling somebody where to go and fix
 * their enquiry. The union makes the wrong call fail to compile.
 */
type SeedMoment = { when: 'before' } | { when: 'after'; raisedADeal: boolean };

/**
 * What the service mapping did — or will do — to this enquiry. **The ONE
 * renderer, mounted at the chooser AND in the report.**
 *
 * 🔴 **WHY IT IS SHOWN BEFORE THE DEAL FORM OPENS — owner's decision, put as
 * *"the skipped-services notice must be shown BEFORE the deal form opens, not
 * only afterwards in the report"*, and answered that way.** The mapping table
 * **ships empty**, so on day one every service on every enquiry is unmapped:
 * `serviceLinesFor` returns no lines, no `prefill` is passed, and the deal form
 * opens with an **empty line grid and nothing on screen explaining why.** The
 * explanation used to arrive only in the report — whose own remedy, *add the
 * lines by hand on the deal*, points at a form that has just closed.
 *
 * ⚠️ **The chooser is the WARNING and the report is the RECORD, and both are
 * kept.** They are two different sentences about one fact, which is exactly why
 * they go through one renderer: two copies would agree the day they were written
 * and drift on the first correction, and what would drift is a count of the work
 * a customer asked for against the work the deal carries.
 *
 * ## 🔴 THE THIRD STATE IS DECIDED HERE AND NOWHERE ELSE
 *
 * `mapsUnread` is ***we did not ask***, which is **not** ***there are no
 * mappings***. The read failed, so this flow cannot say which services were
 * mapped — and naming them all as unmapped on the strength of a request that
 * never came back is an accusation against configuration that may be perfectly
 * complete. The two send a reader to opposite places: one to Service Maps, the
 * other to try again.
 *
 * ⛔ **Deciding it in ONE place is what stops the two moments drifting on it**,
 * and a renderer moved between stages is precisely where that separation gets
 * lost — the branch is easy to leave behind at the stage it was written for.
 *
 * ⚠️ **It is reported EVEN ON A CLEAN RUN**, because it is the one state in
 * which a deal carries NO lines for a reason that has nothing to do with the
 * enquiry. Silence there is a deal that looks deliberately empty.
 */
function ServiceSeedingNotice({
  services,
  moment,
  className,
}: {
  services: ServiceLines;
  moment: SeedMoment;
  /**
   * Spacing from whatever sits above — applied **only when there is something to
   * render.**
   *
   * ⚠️ **A caller cannot ask this component whether it will draw anything**: it
   * returns `null` from two different branches, so a wrapper written at the call
   * site leaves an empty gap on every fully-mapped enquiry. Passing the class in
   * keeps *"is there anything to say"* a single decision, made here, rather than
   * a second copy at the chooser that drifts the first time a branch is added.
   */
  className?: string;
}) {
  if (services.mapsUnread) {
    return (
      <div className={className}>
        <ReportLine
          tone="warning"
          headline={
            moment.when === 'before'
              ? 'The service mapping could not be read, so no lines will be seeded.'
              : 'The service mapping could not be read, so no lines were seeded.'
          }
          notes={[
            moment.when === 'before'
              ? 'This is not a statement about the services on this enquiry — it could not be checked. The deal will open with an empty line grid; add the lines by hand, or close this and try again.'
              : 'This is not a statement about the services on this enquiry — it could not be checked. Add the lines by hand, or try the conversion again.',
          ]}
        />
      </div>
    );
  }

  // Nothing skipped and the mapping was read: the deal carries every service the
  // enquiry named, so there is nothing to say and no box to draw saying it.
  if (services.skipped.length === 0) return null;

  return (
    <div className={className}>
      <SkippedServices services={services} moment={moment} />
    </div>
  );
}

/**
 * The count sentence — **ONE copy of the singular/plural branch, tensed by the
 * moment.**
 *
 * ⛔ **The arithmetic is not duplicated per stage.** What differs between the
 * chooser and the report is a verb, and nothing else; branching the whole
 * sentence per moment would put the count in two places, where a correction to
 * one is a disagreement with the other.
 */
function skippedHeadline(count: number, moment: SeedMoment): string {
  const subject = count === 1 ? '1 service on this enquiry' : `${count} services on this enquiry`;
  if (moment.when === 'before') {
    return count === 1
      ? `${subject} will not be seeded as a line.`
      : `${subject} will not be seeded as lines.`;
  }
  return count === 1 ? `${subject} did not become a line.` : `${subject} did not become lines.`;
}

/**
 * *"It is" / "One is" / "They are" / "Some are"* — **one helper, both reason
 * tails.**
 *
 * ⚠️ **It also closes a hole the report copy carried: `count === total ? 'They
 * are' : 'Some are'` reads *"They are not mapped"* over a list of ONE**, which
 * is the commonest shape there is on an enquiry naming a single service. The
 * all-of-them case is the NORM here, so the plural was right far more often than
 * it was correct.
 */
function reasonLead(count: number, total: number): string {
  if (count < total) return count === 1 ? 'One is' : 'Some are';
  return count === 1 ? 'It is' : 'They are';
}

/**
 * Where an unmapped service sends the reader — **a different place at each
 * moment, which is the one thing that legitimately differs.**
 *
 * ⚠️ **"convert again" is accurate rather than vague: the mapping is read ONCE,
 * by this flow's on-open effect.** Mapping the services while this dialog is
 * open changes nothing on screen; closing it and converting again re-reads them.
 */
function unmappedRemedy(count: number, moment: SeedMoment): string {
  const map = count === 1 ? 'Map it under Service Maps' : 'Map them under Service Maps';
  if (moment.when === 'before') {
    return `${map} and convert again, or add the lines by hand once the deal opens.`;
  }
  return moment.raisedADeal
    ? `${map}, or add the lines by hand on the deal.`
    : `${map}, or add the lines by hand.`;
}

/**
 * The services that did not — or will not — become lines.
 *
 * 🔴 **A SERVICE THAT RESOLVED TO NOTHING IS NAMED, NEVER SILENTLY DROPPED.**
 * Dropping one produces a deal quietly covering less than the customer asked
 * for, with a success at every step and nothing anywhere saying so — and the
 * person who would notice is the one reading the enquiry, on this screen, now.
 *
 * ⛔ **It renders the KEY where the name did not resolve** — never a placeholder
 * word. Coalescing invents a product that does not exist and makes an
 * administrator's missing configuration invisible, which is the one thing the
 * field exists to report.
 *
 * ⛔ **It says nothing about names being on their way.** The mapping table ships
 * empty, so on day one every service on every enquiry is unmapped: that is the
 * expected state rather than a fault.
 *
 * ⚠️ **Reached only through {@link ServiceSeedingNotice}**, which is what
 * guarantees it is never rendered over an unread mapping — the state in which
 * `skipped` is deliberately EMPTY and naming nothing would read as nothing being
 * wrong — and which holds the empty check, so there is no second one here that
 * could never fire.
 */
function SkippedServices({ services, moment }: { services: ServiceLines; moment: SeedMoment }) {
  const { skipped } = services;
  const unmapped = skipped.filter((service) => service.reason === 'unmapped');
  const duplicate = skipped.filter((service) => service.reason === 'duplicate');

  return (
    <Notice tone="warning" title={skippedHeadline(skipped.length, moment)}>

      {/*
        🔴 THE CONSEQUENCE, AND IT IS THE COMMON PATH RATHER THAN AN EDGE. With
        the mapping table empty every service is skipped, so `lines` is empty, no
        `prefill` is passed, and the form opens with nothing in the grid. Said
        HERE, before it happens, because after it has happened the report's own
        deal line already says what was raised — and by then the form is closed.
      */}
      {moment.when === 'before' && services.lines.length === 0 && (
        <p className="mt-1 text-2xs opacity-90">
          Nothing is left to seed, so the deal will open with an empty line grid.
        </p>
      )}

      <ul className="mt-1 space-y-1">
        {skipped.map((service) => {
          /*
           * ⛔ ONE `typeof` READ, TWO USES — and it is the fix for a pair of
           * lines that disagreed with each other. `{service.name ?? service.key}`
           * treats NULLISH as absent while `service.name !== null` does not, so
           * a `name` of `undefined` rendered the key and then the key again in
           * brackets: `masterclasses (masterclasses)`. Every optional field on
           * this wire is read with `typeof` for exactly that reason — an absent
           * key reads `undefined`, and `undefined !== null` is TRUE.
           *
           * ⚠️ Blank is unresolved too, matching `linkRefusalMessage` and the
           * chooser's own meta filter: a name of `''` would render an empty
           * label beside a bracketed key, which reads as a rendering fault
           * rather than as a key nobody has mapped.
           */
          const resolved =
            typeof service.name === 'string' && service.name.trim() !== '' ? service.name : null;
          return (
            <li key={`${service.reason}:${service.key}`} className="text-2xs break-words">
              {/* The name where one resolved, the KEY where none did. */}
              <span className="font-medium">{resolved ?? service.key}</span>
              {resolved !== null && <span className="opacity-80"> ({service.key})</span>}
            </li>
          );
        })}
      </ul>

      {unmapped.length > 0 && (
        <p className="mt-1 text-2xs opacity-90">
          {reasonLead(unmapped.length, skipped.length)} not mapped to an EMERGE product.{' '}
          {unmappedRemedy(unmapped.length, moment)}
        </p>
      )}

      {/*
        ⚠️ ONE SENTENCE FOR BOTH MOMENTS, deliberately. A duplicate is a
        duplicate whether the second line was refused a moment ago or is about to
        be — "another line already carries it" is true of the seeded set and of
        the deal alike — so tensing this one would be a second copy bought for
        nothing.
      */}
      {duplicate.length > 0 && (
        <p className="mt-1 text-2xs opacity-90">
          {reasonLead(duplicate.length, skipped.length)} mapped to a product another line already
          carries, and a deal can hold a product only once.
        </p>
      )}
    </Notice>
  );
}
